import { FC, useMemo } from 'react'

import { TypeStyle } from '@syconium/little-miss-figgy'

import { useOrganization } from '../../../app/_providers/OrganizationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../containers/EmbroideryContainer/EmbroideryProvider'

import { EmbroideryCta } from './EmbroideryCta'
import { PersonalizeSection } from './PersonalizeSection'
import { TextEmbroiderySection } from './TextEmbroiderySection'

interface Props {
  isB2BColorSelected?: boolean
  isPortalSelectionInvalid?: boolean
  hasBorder?: boolean
}

export const EmbroideryCtaSection: FC<Props> = ({
  isB2BColorSelected,
  isPortalSelectionInvalid,
  hasBorder = true,
}) => {
  const { b2bStorefrontCollection } = useOrganization()
  const { requiredEmbroideryTypes } = useEmbroidery()
  const { embroidery: embroideryStrings } = useTranslation()

  // Check if current portal enforces embroidery constraints for the product category
  const mandatoryTextEmbroidery = requiredEmbroideryTypes.includes('TEXT')
  const mandatoryLogoEmbroidery = requiredEmbroideryTypes.includes('LOGO')

  // Create an array of copy lines to display for the "personalize" section
  const personalizeCopyLines = useMemo(() => {
    // If the current color isn't a portal color & no mandatory restrictions are defined for embroidery,
    // don't return any copy for the personalize section
    if (!isB2BColorSelected || (!mandatoryLogoEmbroidery && !mandatoryTextEmbroidery)) return []

    const orgOrPortalName = b2bStorefrontCollection?.name

    // Compute the first copy line
    const topLine = (() => {
      let copy = null
      if (mandatoryTextEmbroidery && mandatoryLogoEmbroidery)
        copy = embroideryStrings.personalizeSection.logoAndTextEmbroideryRequired
      else if (mandatoryLogoEmbroidery)
        copy = embroideryStrings.personalizeSection.logoEmbroideryRequired
      else if (mandatoryTextEmbroidery)
        copy = embroideryStrings.personalizeSection.textEmbroideryRequired

      if (!copy) return null
      return `${orgOrPortalName} ${copy}.`
    })()

    const placementNoticeLine = embroideryStrings.personalizeSection.embroideryPlacementNotice

    return [topLine, placementNoticeLine].filter(Boolean)
  }, [
    isB2BColorSelected,
    mandatoryLogoEmbroidery,
    mandatoryTextEmbroidery,
    b2bStorefrontCollection?.name,
    embroideryStrings.personalizeSection.embroideryPlacementNotice,
    embroideryStrings.personalizeSection.logoAndTextEmbroideryRequired,
    embroideryStrings.personalizeSection.logoEmbroideryRequired,
    embroideryStrings.personalizeSection.textEmbroideryRequired,
  ])

  const displayPortalEmbroideryFeatures =
    isB2BColorSelected && (mandatoryLogoEmbroidery || mandatoryTextEmbroidery)

  return (
    <>
      <PersonalizeSection.Wrapper hasBorder={hasBorder}>
        {displayPortalEmbroideryFeatures ? (
          <PersonalizeSection.Text>
            {personalizeCopyLines.map(copy => (
              <TypeStyle.BodySmall asTag='p' fontWeight={500} key={copy}>
                {copy}
              </TypeStyle.BodySmall>
            ))}
          </PersonalizeSection.Text>
        ) : (
          <EmbroideryCta showLowestPrice />
        )}
      </PersonalizeSection.Wrapper>
      {displayPortalEmbroideryFeatures && (
        <TextEmbroiderySection
          isInvalid={isPortalSelectionInvalid}
          isRequired={mandatoryTextEmbroidery}
          isB2BColorSelected={isB2BColorSelected}
        />
      )}
    </>
  )
}
